'use client';

import { ChangeEvent } from 'react';
import { FormikValues, useFormik } from 'formik';
import * as Yup from 'yup';

import {
  Box,
  BoxProps,
  Button,
  Flex,
  FormControl,
  Text,
  useTheme
} from '@chakra-ui/react';

import { TextField } from 'ui';

const validationSchema = Yup.object().shape({
  promoCode: Yup.string()
});

const initialValues: FormikValues = {
  promoCode: ''
};

interface PromoCodeProps extends BoxProps {
  onSaveDiscount: (promoCode: string) => void;
  onClearError: () => void;
  title?: string;
  variant?: 'light' | 'dark';
  isError: boolean;
  inputProps?: BoxProps;
}

const PromoCode = ({
  onSaveDiscount,
  onClearError,
  isError,
  variant = 'light',
  title = 'Have a Promo Code?',
  inputProps = {},
  ...props
}: PromoCodeProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepReview: { promo }
        }
      }
    },
    fonts
  } = useTheme();

  const {
    errors,
    isSubmitting,
    isValid,
    dirty,
    values,
    touched,
    resetForm,
    setFieldValue,
    setTouched,
    setErrors,
    handleSubmit,
    getFieldProps
  } = useFormik({
    validationSchema,
    validateOnChange: false,
    initialValues,
    onSubmit: async (formData, actions) => {
      await onSaveDiscount(formData.promoCode);
      actions.setSubmitting(false);
      setTouched({ promoCode: true });
      resetForm();
    }
  });
  const isInvalid = !isValid || !dirty || isError;
  const errorMessage = isError ? 'Invalid promo code' : errors.promoCode;
  const isLightVariant = variant === 'light';

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value }
    } = event;
    setFieldValue(name, value);
    if (!isValid || isError) {
      setErrors({});
      onClearError();
    }
  };

  const resetFieldHandler = (field: string) => () => {
    setFieldValue(field, '');
    onClearError();
  };

  return (
    <Box
      as='form'
      onSubmit={handleSubmit}
      display='flex'
      alignItems='center'
      justifyContent='flex-end'
      bg={promo.background}
      w='100%'
      gap='25px'
      py='8px'
      px='12px'
      textAlign='right'
      {...props}
    >
      <Text
        display={{ base: 'none', md: 'block' }}
        fontSize={{ sm: '16px', md: '18px', lg: '20px' }}
        textTransform='uppercase'
        color={isLightVariant ? promo.textLightColor : promo.textColor}
        fontWeight={isLightVariant ? '400' : '600'}
        whiteSpace='nowrap'
      >
        {title}
      </Text>

      <Flex
        gap='12px'
        alignItems='center'
        justifyContent={{ base: 'space-between', md: 'flex-end' }}
        w={{ base: '100%', md: 'auto' }}
        flexShrink={0}
        flexGrow={1}
        maxWidth={{ md: '450px' }}
      >
        <FormControl isInvalid={!isInvalid} maxW={{ md: '330px' }} w='100%'>
          <TextField
            {...inputProps}
            {...getFieldProps('promoCode')}
            placeholder='Enter Promo Code'
            error={errorMessage}
            touched={!!touched.promoCode}
            onReset={resetFieldHandler('promoCode')}
            isFieldValue={!!values.promoCode}
            onChange={handleInputChange}
            height={{ base: '40px !important', md: '50px !important' }}
            rightElementProps={{ height: { base: '40px', md: '50px' } }}
            containerStyles={{ bg: 'white', borderRadius: '8px' }}
            inputStyles={{
              fontFamily: fonts.main,
              fontSize: '20px',
              color: promo.inputColor,
              borderRadius: '8px',
              paddingLeft: '15px',
              fontWeight: '700'
            }}
            _placeholder={{
              textTransform: 'uppercase',
              color: promo.placeholderColor,
              fontSize: '20px',
              fontWeight: '500',
              fontFamily: 'inherit'
            }}
          />
        </FormControl>
        <Button
          colorScheme='black'
          isLoading={isSubmitting}
          type='submit'
          isDisabled={!values.promoCode || isSubmitting}
          borderRadius='6px'
          h={{ base: '40px', md: '50px' }}
          fontSize='20px'
          variant={isLightVariant ? 'outline' : 'solid'}
          w='125px'
          minW='125px'
          fontWeight='600'
          textTransform='uppercase'
        >
          Apply
        </Button>
      </Flex>
    </Box>
  );
};

export default PromoCode;
